import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Tag, Tabs } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import { getUniformItemDetailApi, getPositionMappingsApi, createPositionMappingApi, deletePositionMappingApi } from 'api/services/uniformManagement';
import { formatDateHandler } from 'utils/common';
import { uniformGenderOptions } from '../../constants';
import FilterSelect from 'components/Form/filterSelect';
import { useSelector } from 'react-redux';
import DeleteModal from 'components/modelpopup/DeleteModal';

const UniformItemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { position: positions } = useSelector((state) => state.common);

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [addingMapping, setAddingMapping] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const positionOptions = (positions || []).map(p => ({ label: p.name, value: p.id }));

  const fetchItem = () => {
    setLoading(true);
    getUniformItemDetailApi(id).then(response => {
      setItem(response.data?.data || null);
      setLoading(false);
    }).catch(() => { setLoading(false); navigate('/uniform-management'); });
  };

  const fetchMappings = () => {
    setMappingsLoading(true);
    getPositionMappingsApi({ uniform_item: id }).then(response => {
      setMappings(response.data?.data || []);
      setMappingsLoading(false);
    }).catch(() => setMappingsLoading(false));
  };

  useEffect(() => {
    fetchItem();
    fetchMappings();
  }, [id]);

  const handleAddMapping = () => {
    if (!selectedPosition) return;
    setAddingMapping(true);
    createPositionMappingApi({ uniform_item: parseInt(id), position: selectedPosition.value }).then(() => {
      toast.success('Position mapping added', { position: "top-right", autoClose: 3000, transition: Bounce });
      setSelectedPosition(null);
      setAddingMapping(false);
      fetchMappings();
    }).catch(e => {
      setAddingMapping(false);
      const msg = e.response?.data?.detail || e.response?.data?.non_field_errors?.[0] || 'Failed to add mapping';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const handleDeleteMapping = () => {
    deletePositionMappingApi(deleteId).then(() => {
      toast.success('Mapping removed', { position: "top-right", autoClose: 3000, transition: Bounce });
      setDeleteModal(false);
      setDeleteId(null);
      fetchMappings();
    }).catch(() => {
      toast.error('Failed to remove mapping', { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const getGenderLabel = (val) => uniformGenderOptions.find(o => o.value === val)?.label || val;

  if (loading || !item) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="text-center py-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
        </div>
      </div>
    );
  }

  const mappingColumns = [
    { title: 'Position', dataIndex: 'position_name' },
    {
      title: 'Action',
      render: (_, record) => (
        <Button icon="fa fa-trash" className="btn btn-sm btn-outline-danger"
          onClick={() => { setDeleteId(record.id); setDeleteModal(true); }} />
      )
    }
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
<h3 className="d-inline">{item.name}</h3>
          </div>
        </div>

        <div className="row">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <strong>Gender:</strong> <span className="ms-2">{getGenderLabel(item.gender)}</span>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Color:</strong> <span className="ms-2">{item.color || '-'}</span>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Rotation Cycle:</strong> <span className="ms-2">{item.rotation_cycle_months ? `${item.rotation_cycle_months} months` : '-'}</span>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Status:</strong>
                    <span className={`badge ms-2 ${item.status ? 'bg-success-light' : 'bg-danger-light'}`}>
                      {item.status ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="col-md-12 mb-3">
                    <strong>Description:</strong> <span className="ms-2">{item.description || '-'}</span>
                  </div>
                  <div className="col-md-12 mb-3">
                    <strong>Sizes:</strong>
                    <span className="ms-2">
                      {item.sizes?.map(s => <Tag key={s.id}>{s.name} ({s.code})</Tag>)}
                    </span>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Created:</strong> <span className="ms-2">{formatDateHandler(item.created_at)}</span>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Updated:</strong> <span className="ms-2">{formatDateHandler(item.updated_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {item.image && (
            <div className="col-md-4">
              <div className="card">
                <div className="card-body text-center">
                  <img src={item.image} alt={item.name} style={{ maxWidth: '100%', maxHeight: 250, objectFit: 'contain' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="card mt-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Position Mappings</h5>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-4">
                <FilterSelect
                  isClearable={true}
                  onChange={setSelectedPosition}
                  optionLabel="label"
                  optionValue="value"
                  options={positionOptions}
                  isLoading={false}
                  value={selectedPosition}
                  placeholder="Select Position"
                />
              </div>
              <div className="col-md-2">
                <Button
                  label="Add"
                  icon="fa fa-plus"
                  className="btn btn-primary btn-sm"
                  loading={addingMapping}
                  disabled={!selectedPosition || addingMapping}
                  onClick={handleAddMapping}
                />
              </div>
            </div>
            <Table
              columns={mappingColumns}
              dataSource={mappings}
              rowKey={(record) => record.id}
              loading={mappingsLoading}
              pagination={false}
              scroll={{ x: "max-content" }}
            />
          </div>
        </div>

        <DeleteModal
          Name="Remove Position Mapping"
          deleteModal={deleteModal}
          setDeleteModal={setDeleteModal}
          deleteHandler={handleDeleteMapping}
        />
      </div>
    </div>
  );
};

export default UniformItemDetail;
